<script>
import gql from 'graphql-tag'
import { mapState } from 'vuex'

import { Plugins } from '@capacitor/core'
const { Haptics } = Plugins

import PaymentMethod from '@/components/payment/PaymentMethod'

import { PAYMENT_METHODS_QUERY } from '@/graph/gql/payment.gql'
import { deletePaymentMethod } from '@/graph/mutations/payment'

const REMOVE_METHOD_TIMEOUT = 500
let timer = null

export default {
  name: 'PaymentView',
  components: { PaymentMethod },
  props: {
    fromCheckout: { type: Boolean, default: () => false }
  },
  apollo: {
    paymentMethods: {
      query: PAYMENT_METHODS_QUERY,
      fetchPolicy: 'cache-first'
    },
    user: {
      query: gql`
        query {
          user: getViewer {
            id
            credits
          }
        }
      `,
      fetchPolicy: 'cache-first'
    }
  },
  data: () => ({
    deleteDialog: false,
    deleteMethod: null,
    scrolling: false,
    removing: false
  }),
  computed: {
    ...mapState({
      cartPaymentMethod: state => state.cart.paymentMethod,
      chef: state => state.cart.chef,
      cartItems: state => state.cart.items
    })
  },
  methods: {
    // Will start timer on touchstart
    // After timeout delete payment method
    startRemoveTimer(method) {
      timer = setTimeout(() => {
        if (this.scrolling) return

        Haptics.vibrate()
        this.openDeleteDialog(method)
      }, REMOVE_METHOD_TIMEOUT)
    },

    // Reset timer
    stopTimer(method) {
      clearTimeout(timer)

      const { chef, cartItems } = this
      if (this.fromCheckout || (chef && cartItems.length)) {
        this.selectMethod(method)
      }

      this.scrolling = false
    },

    selectMethod(method) {
      this.$store.commit('cart/setPaymentMethod', method)
      this.$router.replace({ name: 'CheckoutView' })
    },

    openDeleteDialog(method) {
      this.deleteDialog = true
      this.deleteMethod = method
    },

    closeDeleteDialog() {
      this.deleteDialog = false
      this.deleteMethod = null
    },

    removeMethod() {
      if (!this.deleteMethod || this.removing) return
      this.removing = true

      const { id } = this.deleteMethod
      deletePaymentMethod(id)
        .then(() => {
          this.paymentMethods = this.paymentMethods.filter(x => x.id !== id)

          if (this.cartPaymentMethod && this.cartPaymentMethod.id === id) {
            this.$store.commit('cart/setPaymentMethod', null)
          }

          this.$toast('Payment method deleted')
        })
        .finally(this.closeDeleteDialog)
        .finally(() => {
          this.removing = false
        })
    }
  }
}
</script>

<template>
  <transition name="slide-up" appear>
    <!-- Checkout view page -->
    <div class="view-grid">
      <div class="content-top px-3">
        <div class="display-1 font-weight-bold px-2 mb-2">
          Payment
        </div>

        <v-skeleton-loader
          :loading="$apollo.loading"
          transition="fade-transition"
          type="list-item-avatar-two-line"
        >
          <v-layout row wrap>
            <v-list>
              <v-list-item
                v-for="method in paymentMethods"
                :key="method.id"
                v-ripple="{ class: 'secondary--text' }"
                class="px-5"
                @mouseup.prevent="stopTimer(method)"
                @touchend.prevent="stopTimer(method)"
                @mousedown.prevent="startRemoveTimer(method)"
                @touchstart="startRemoveTimer(method)"
                @touchmove="scrolling = true"
              >
                <payment-method
                  :data="method"
                  :selected="
                    cartPaymentMethod && cartPaymentMethod.id === method.id
                  "
                />
              </v-list-item>
            </v-list>
          </v-layout>
        </v-skeleton-loader>

        <div v-if="!$apollo.loading">
          <!-- If empty -->
          <div v-if="!paymentMethods.length" class="methods-empty">
            <span class="subtitle-1 font-weight-bold">
              No payment method
            </span>
            <p class="body-2">
              Add a credit card so you don't<br />
              miss out on your next meal.
            </p>
            <v-btn
              rounded
              depressed
              color="primary"
              class="mt-3"
              :to="{ name: 'AddPaymentMethodView' }"
            >
              <v-icon size="16" class="mr-2">$vuetify.icons.plus</v-icon>
              Add payment method
            </v-btn>
          </div>

          <div v-else class="mt-2 px-2">
            <v-btn
              rounded
              outlined
              class="primary--text px-6"
              :to="{ name: 'AddPaymentMethodView' }"
            >
              Add payment method
            </v-btn>
          </div>
        </div>

        <div
          v-if="!$apollo.loading && paymentMethods.length && user"
          class="mt-7"
        >
          <v-layout row wrap>
            <v-list>
              <v-list-item v-if="user" class="px-5">
                <payment-method
                  credits
                  :data="{
                    value: user.credits || 0,
                    currencyCode: ''
                  }"
                />
              </v-list-item>
            </v-list>
          </v-layout>

          <v-btn
            rounded
            outlined
            class="black--text px-6 mx-2 mb-6"
            :to="{ name: 'PromotionsView' }"
          >
            Promotions
          </v-btn>
        </div>

        <!-- Delete dialog -->
        <v-dialog v-model="deleteDialog">
          <v-card>
            <v-card-title>Remove payment method?</v-card-title>

            <v-card-actions>
              <v-spacer />
              <v-btn
                :disabled="removing"
                color="blue-grey darken-1"
                text
                @click="closeDeleteDialog()"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="removing"
                color="pink darken-1"
                text
                @click="removeMethod()"
              >
                Remove
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </transition>
</template>

<style lang="sass" scoped>
.v-skeleton-loader--is-loading
  padding: 0 8px

.v-list
  width: 100%

.methods-empty
  position: relative
  height: 100%
  width: 100%
  text-align: center
  transform: translate(0%, 90%)
</style>
